import { fetchAEMContent } from "@AEM/AEMContentLoader"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { useAEMLanguageData } from "@AEM/hooks/useAEMLanguageData"
import { AEMPageType } from "@AEM/models/AEMPage.model"
import type { AEMDictionary } from "@AEM/models/AEMResponse.model"
import { showErrorNotification, showSuccessNotification } from "@components/ui"
import { useAuthContext } from "@context/AuthProvider"
import { useUserStore } from "@hooks/useUserStore"
import { UserProfile } from "@models/userProfile.model"
import { AuthService } from "@services/authService"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { setGlobalLocale } from "@utils/locale"
import { StorageKeys } from "@utils/user-config"
import secureLocalStorage from "react-secure-storage"
import {
	addAgencyAffiliation,
	setActiveAgencyApi,
	setDefaultAgencyApi,
	UpdateAgentRequest,
	updateAgent
} from "../services/agentService"

export const useAgentUpdate = () => {
	const { refreshUserProfile } = useAuthContext()
	const setUserProfile = useUserStore((state) => state.setUserProfile)
	const queryClient = useQueryClient()
	const { dictionary, refetch: refetchDictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const { languageData } = useAEMLanguageData()

	const getLocaleFromLanguage = (language?: string) => {
		if (!language) return undefined
		return languageData
			?.find((item) =>
				item.languageCode
					?.toLowerCase()
					.startsWith(`${language.toLowerCase()}_`)
			)
			?.languageCode?.toLowerCase()
	}

	const getSuccessMessage = async (locale?: string) => {
		if (locale) {
			const profile = useUserStore.getState().userProfile
			try {
				const nextDictionary = await fetchAEMContent<AEMDictionary>(
					AEMPageType.DICTIONARY,
					{
						queryParams: {
							name: AEM_DICTIONARY_COLLECTIONS.REGISTRATION,
							locale,
							role: profile?.roleCode,
							office: profile?.reportingOfficeCode,
							country: profile?.countryCode
						}
					}
				)
				return nextDictionary.yourProfileHasBeenSucceessMessage?.trim()
			} catch {
				return undefined
			}
		}

		return (
			dictionary?.yourProfileHasBeenSucceessMessage?.trim() ||
			(
				await refetchDictionary()
			).data?.yourProfileHasBeenSucceessMessage?.trim()
		)
	}

	const mutation = useMutation({
		mutationFn: (vars: { agentId: number; payload: UpdateAgentRequest }) =>
			updateAgent(vars.agentId, vars.payload),
		onMutate: ({ payload }): { previousProfile: UserProfile | null } => {
			const previousProfile = useUserStore.getState().userProfile
			if (!previousProfile) return { previousProfile: null }

			setUserProfile({
				...previousProfile,
				agentFirstName: payload.firstName ?? previousProfile.agentFirstName,
				agentLastName: payload.lastName ?? previousProfile.agentLastName,
				preferredLanguage:
					payload.espressoLanguage ?? previousProfile.preferredLanguage,
				agencyRole: payload.agencyRole ?? previousProfile.agencyRole,
				agencyBusinessModel:
					payload.agencyBusinessModel ?? previousProfile.agencyBusinessModel,
				jobTitle: payload.jobTitle ?? previousProfile.jobTitle,
				yearsIndustry: payload.yearsIndustry ?? previousProfile.yearsIndustry,
				agentCountryCode:
					payload.countryCode ?? previousProfile.agentCountryCode,
				zipCode: payload.zipCode ?? previousProfile.zipCode,
				consortialOrHostFlag:
					payload.consortialOrHost ?? previousProfile.consortialOrHostFlag,
				consortialOrHostName:
					payload.consortialOrHostName ?? previousProfile.consortialOrHostName,
				clia: payload.cliaAgentId ?? previousProfile.clia,
				colonialId: payload.colonialID ?? previousProfile.colonialId,
				regions: payload.regions
					? payload.regions.map(Number)
					: previousProfile.regions,
				secondSpecialization:
					payload.secondSpecialization ?? previousProfile.secondSpecialization,
				thirdSpecialization:
					payload.thirdSpecialization ?? previousProfile.thirdSpecialization,
				languageCode: payload.languageCode ?? previousProfile.languageCode,
				localeCountryCode:
					payload.localeCountryCode ?? previousProfile.localeCountryCode
			})

			return { previousProfile }
		},
		onError: (_err, _vars, ctx) => {
			if (ctx?.previousProfile) setUserProfile(ctx.previousProfile)
			showErrorNotification(dictionary?.mfaSaveChangesErrorMsg?.trim() ?? "")
		},
		onSuccess: async (_data, { payload }) => {
			queryClient.invalidateQueries({ queryKey: ["agentMe"] })
			refreshUserProfile()

			const nextLocale = getLocaleFromLanguage(payload.espressoLanguage)
			if (nextLocale) setGlobalLocale(nextLocale)

			const successMessage = await getSuccessMessage(nextLocale)
			if (successMessage) {
				showSuccessNotification(successMessage)
			}
		}
	})

	return {
		updateAgent: mutation.mutate,
		updateAgentAsync: mutation.mutateAsync,
		isUpdating: mutation.isPending,
		error: mutation.error,
		reset: mutation.reset
	}
}

export const useAddAffiliation = () => {
	const { refreshUserProfile } = useAuthContext()

	const mutation = useMutation({
		mutationFn: async (agencyId: number) =>
			await addAgencyAffiliation(agencyId),
		onSuccess: async () => {
			await refreshUserProfile()
		}
	})

	return {
		addAffiliation: mutation.mutateAsync,
		isAdding: mutation.isPending,
		error: mutation.error,
		reset: mutation.reset
	}
}

export const useSetActiveAgency = () => {
	const { setUserProfile } = useUserStore()

	const appQueryClient = useQueryClient()

	const mutation = useMutation({
		mutationFn: async (agencyId: number) => {
			// Set localStorage before the PUT so x-active-agency header is sent
			secureLocalStorage.setItem(StorageKeys.ACTIVE_AGENCY_ID, String(agencyId))
			return await setActiveAgencyApi(agencyId)
		},
		onSuccess: async () => {
			try {
				const updatedProfile = await AuthService.buildUserProfile()
				setUserProfile(updatedProfile)
				appQueryClient.invalidateQueries()
			} catch (error) {
				console.error(
					"Failed to refresh profile/cache after agency switch",
					error
				)
			}
		},
		onError: () => {
			// Roll back localStorage to the current active affiliation
			const active = useUserStore
				.getState()
				.availableAffiliations.find((a) => a.active)
			if (active) {
				secureLocalStorage.setItem(
					StorageKeys.ACTIVE_AGENCY_ID,
					String(active.agencyId)
				)
			}
		}
	})

	return {
		activateAgencyAsync: mutation.mutateAsync,
		isActivating: mutation.isPending,
		error: mutation.error,
		reset: mutation.reset
	}
}

export const useSetDefaultAgency = () => {
	const mutation = useMutation({
		mutationFn: async (agencyId: number) => await setDefaultAgencyApi(agencyId)
	})

	return {
		setDefaultAgency: mutation.mutateAsync,
		isSettingDefault: mutation.isPending,
		error: mutation.error,
		reset: mutation.reset
	}
}
